import { MetroStationHeader } from '@metro/station-details'
import { useRef, useState } from 'react'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import type { LineStatusEntry } from '../components/stationPage/fetchLineStatuses'
import { lineStatusTone } from '../components/stationPage/lineStatusTone'
import { useSwipeDownDismiss } from '../components/useSwipeDownDismiss'
import type { LineView } from './buildLineView'
import { LineDetailsPanel } from './LineDetailsPanel'
import type { LineViewActions } from './useLineViewActions'
import { useLineTitle } from './useLineTitle'
import styles from './LineCardOverlay.module.css'

/** Upward drag (px) on the collapsed card that opens the sheet (legend C·4). */
const EXPAND_DRAG_PX = 40

export interface LineCardOverlayProps {
  readonly view: LineView
  readonly status: LineStatusEntry | null | undefined
  readonly actions: LineViewActions
}

/**
 * The line view on a phone. Collapsed (Figma C, `9242:16993`): a station-card
 * shaped card — line header with the live status as subtitle, "Stations",
 * and the in-app CTA; swipe it down to close the line view, up (or
 * "Stations") to open the sheet. Expanded (D, `9242:17014`): a sheet over
 * the lower map with the full {@link LineDetailsPanel}; drag it down to
 * return to the card. A station row closes the sheet and opens the station.
 */
export function LineCardOverlay({
  view,
  status,
  actions,
}: LineCardOverlayProps): React.ReactElement {
  const { t } = useTranslation('line-details')
  const title = useLineTitle(view)
  const [expanded, setExpanded] = useState(false)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const card = useSwipeDownDismiss(cardRef, actions.onClose)
  const sheet = useSwipeDownDismiss(sheetRef, () => setExpanded(false))
  const dragStartY = useRef<number | null>(null)

  if (expanded) {
    return (
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${sheet.springing ? styles.springing : ''}`}
        style={sheet.sheetStyle}
        role="dialog"
        aria-label={title}
        data-testid="line-sheet"
      >
        <div className={styles.dragZone} data-testid="line-sheet-drag-zone" {...sheet.handlers}>
          <div className={styles.handle} aria-hidden="true" />
        </div>
        <div className={styles.scrollArea}>
          <LineDetailsPanel
            view={view}
            status={status}
            actions={{
              ...actions,
              onStationTap: (stationId) => {
                setExpanded(false)
                actions.onStationTap(stationId)
              },
            }}
          />
        </div>
      </div>
    )
  }

  const tone = lineStatusTone(status?.severity, status?.description)
  const subtitle =
    status === undefined
      ? t('stations_count', { count: view.stationCount })
      : (tone.rawLabel ?? t(tone.tone === 'good' ? 'status_good' : 'status_disruption'))
  const zoneLabel = t('zone_label', { count: view.zoneCount })
  return (
    <div className={styles.overlay} data-testid="line-card-overlay">
      <div
        ref={cardRef}
        className={`${styles.card} ${card.springing ? styles.springing : ''}`}
        style={card.sheetStyle}
        data-testid="line-card"
      >
        {/* The header is the drag zone — capturing the pointer on the whole
            card would swallow the buttons' clicks. */}
        <div
          className={styles.dragHeader}
          data-testid="line-card-drag-zone"
          onPointerDown={(e) => {
            dragStartY.current = e.clientY
            card.handlers.onPointerDown(e)
          }}
          onPointerMove={card.handlers.onPointerMove}
          onPointerUp={(e) => {
            const start = dragStartY.current
            dragStartY.current = null
            if (start !== null && start - e.clientY > EXPAND_DRAG_PX) setExpanded(true)
            card.handlers.onPointerUp(e)
          }}
          onPointerCancel={card.handlers.onPointerCancel}
        >
          <MetroStationHeader
            title={title}
            subtitle={subtitle}
            badge={view.zones}
            lineHexColor={view.color}
            zoneLabel={zoneLabel}
            zoneAria={(zones) => `${zones} ${zoneLabel}`}
          />
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.primaryButton}
            data-testid="line-card-stations"
            onClick={() => setExpanded(true)}
          >
            {t('card_stations')}
          </button>
          {actions.ctaHref !== null && (
            <a
              className={styles.secondaryButton}
              href={actions.ctaHref}
              onClick={actions.onCtaTap}
              data-testid="line-card-app-cta"
            >
              {t('card_cta', { line: title })}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
